const STORAGE_PEPPER = "architecture-one-sd-storage-v1";
const SIG_SUFFIX = ":__sig";
export const SD_ROLE_STORAGE_KEY = "sd_role";

/** Keys that must only be written through secure helpers. */
export const PROTECTED_LOCAL_STORAGE_KEYS = new Set([
  "accessToken",
  "refreshToken",
  "userInfo",
  "tenantId",
  SD_ROLE_STORAGE_KEY,
]);

/** Logout when these are tampered — role is always re-fetched from API, not trusted from storage. */
const AUTH_INTEGRITY_KEYS = new Set([
  "accessToken",
  "refreshToken",
  "userInfo",
  "tenantId",
]);

let internalWrite = false;
let guardInstalled = false;

function fnv1aHash(input: string): string {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function signature(key: string, value: string, binding = ""): string {
  return fnv1aHash(`${STORAGE_PEPPER}|${key}|${value}|${binding}`);
}

function sigKey(key: string): string {
  return `${key}${SIG_SUFFIX}`;
}

/** Bind role to refresh token so role cannot be changed independently. */
function sessionBinding(): string {
  if (typeof window === "undefined") return "";
  const refresh = localStorage.getItem("refreshToken");
  if (refresh) return refresh.slice(-48);
  const access = localStorage.getItem("accessToken");
  if (access) return access.slice(-48);
  return "";
}

function withInternalWrite(action: () => void): void {
  internalWrite = true;
  try {
    action();
  } finally {
    internalWrite = false;
  }
}

export type SecureReadResult =
  | { ok: true; value: string }
  | { ok: false; reason: "missing" | "tampered" };

function hasAnySecureSignature(): boolean {
  for (const protectedKey of PROTECTED_LOCAL_STORAGE_KEYS) {
    if (localStorage.getItem(sigKey(protectedKey))) return true;
  }
  return false;
}

function readRaw(key: string): SecureReadResult {
  if (typeof window === "undefined") return { ok: false, reason: "missing" };

  const value = localStorage.getItem(key);
  if (value === null) return { ok: false, reason: "missing" };

  const storedSig = localStorage.getItem(sigKey(key));
  const bindSession = key === SD_ROLE_STORAGE_KEY;
  const binding = bindSession ? sessionBinding() : "";
  const expectedSig = signature(key, value, binding);

  if (!storedSig) {
    const sessionStarted = hasAnySecureSignature() || Boolean(sessionBinding());
    if (sessionStarted) {
      return { ok: false, reason: "tampered" };
    }
    // Legacy plain value — sign once so existing sessions keep working.
    withInternalWrite(() => {
      localStorage.setItem(sigKey(key), expectedSig);
    });
    return { ok: true, value };
  }

  if (storedSig !== expectedSig) {
    return { ok: false, reason: "tampered" };
  }

  return { ok: true, value };
}

export function setSecureLocalItem(
  key: string,
  value: string,
  options?: { bindSession?: boolean }
): void {
  if (typeof window === "undefined") return;

  const bindSession = options?.bindSession ?? key === SD_ROLE_STORAGE_KEY;
  const binding = bindSession ? sessionBinding() : "";

  withInternalWrite(() => {
    localStorage.setItem(key, value);
    localStorage.setItem(sigKey(key), signature(key, value, binding));
  });
}

export function getSecureLocalItem(key: string): string | null {
  const result = readRaw(key);
  return result.ok ? result.value : null;
}

export function removeSecureLocalItem(key: string): void {
  if (typeof window === "undefined") return;
  withInternalWrite(() => {
    localStorage.removeItem(key);
    localStorage.removeItem(sigKey(key));
  });
}

/** Returns false when token/user storage was edited outside secure helpers. */
export function verifySecureAuthStorage(): boolean {
  if (typeof window === "undefined") return true;

  for (const key of AUTH_INTEGRITY_KEYS) {
    const value = localStorage.getItem(key);
    if (value === null) continue;

    const result = readRaw(key);
    if (!result.ok && result.reason === "tampered") {
      return false;
    }
  }
  return true;
}

/** Re-sign sd_role after access/refresh token rotation. Uses raw storage — safe even when the old signature is stale. */
export function rebindSecureRoleAfterAuthTokenUpdate(): void {
  if (typeof window === "undefined") return;
  const role = localStorage.getItem(SD_ROLE_STORAGE_KEY);
  if (role) rebindSecureRole(role);
}

/** Re-sign role after token refresh so session binding stays valid. */
export function rebindSecureRole(role: string): void {
  if (!role) {
    removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
    return;
  }
  setSecureLocalItem(SD_ROLE_STORAGE_KEY, role, { bindSession: true });
}

/**
 * Block casual DevTools `localStorage.setItem('sd_role', ...)` writes.
 * Application-tab direct edits are still caught on read via signature checks.
 */
export function installSecureLocalStorageGuard(): void {
  if (typeof window === "undefined" || guardInstalled) return;
  guardInstalled = true;

  const originalSetItem = Storage.prototype.setItem;
  Storage.prototype.setItem = function setItemGuard(
    key: string,
    value: string
  ) {
    if (!internalWrite && PROTECTED_LOCAL_STORAGE_KEYS.has(key)) {
      console.warn(
        `[Service Desk] Blocked direct localStorage write to protected key "${key}".`
      );
      return;
    }
    return originalSetItem.call(this, key, value);
  };
}
